import { useEffect } from "react";

/**
 * Synchronizes vertical scrolling between two container refs.
 * When either container scrolls, the other mirrors its scrollTop.
 *
 * Used by rating tables to keep the fixed student-name column and
 * the scrollable rating column in vertical alignment.
 *
 * @param {React.RefObject} fixedRef   - the fixed (left) container
 * @param {React.RefObject} scrollRef  - the scrollable (right) container
 * @param {Array}           deps       - additional deps to re-attach listeners
 *   (e.g. [imagesLoading] — re-attach after images finish loading)
 */
const useScrollSync = (fixedRef, scrollRef, deps = []) => {
    useEffect(() => {
        const fixed = fixedRef.current;
        const scroll = scrollRef.current;
        if (!fixed || !scroll) return;

        const syncFixed = () => {
            scroll.scrollTop = fixed.scrollTop;
        };
        const syncScroll = () => {
            fixed.scrollTop = scroll.scrollTop;
        };

        scroll.addEventListener("scroll", syncScroll);
        fixed.addEventListener("scroll", syncFixed);

        return () => {
            scroll.removeEventListener("scroll", syncScroll);
            fixed.removeEventListener("scroll", syncFixed);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixedRef, scrollRef, ...deps]);
};

export default useScrollSync;
